/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: CrudIntegration
 * Reason: "Berufe (TAA)" tab of the unified xx Side-Car Playground. Picks an xx.XxPerson of the
 *         PLAYGROUND_TypeA type and edits its internal typed side-car (xx.XxPersonData) through the
 *         framework's generic CRUD form. The occupation field renders as the framework's
 *         search-backed value list control (uiInputControl = valueListSearch) — this component does
 *         NOT render any field itself. Both host foreign keys (person + type) are fixed and hidden
 *         via importObjectContextDto, because they are the unique key of the side-car instance.
 */
import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from 'rxjs';
import {ObjectIdentifier} from '../../../../../core/shared/basic/object-identifier';
import {DtoDetail} from '../../../../../core/shared/dto/dto.detail';
import {DtoImportObjectContext} from '../../../../../core/shared/dto/dto.import.object.context';
import {
    ObjectChangeInformation,
    ObjectChangeInformationActionEnum
} from '../../../../../core/shared/logic/object-change-information';
import {XxPersonDto} from '../../../model/dto/entity/xx-person.dto';
import {XxPersonTypeDto} from '../../../model/dto/entity/xx-person-type.dto';
import {XxPersonTypeFieldDto} from '../../../model/dto/entity/xx-person-type-field.dto';
import {XxSidecarPlaygroundService} from '../../../service/xx-sidecar-playground.service';

/**
 * Name of the demo person type this tab operates on. Resolved BY NAME at runtime — never by a
 * hardcoded database id, because the seeded ids differ between environments.
 */
const XX_PLAYGROUND_PERSON_TYPE_NAME = 'PLAYGROUND_TypeA';

/**
 * Object type alias of the internal typed side-car INSTANCE that is edited here. It is registered in
 * `xx.entity-provider.ts`, which is what lets `MvsCoreService` resolve its CRUD service.
 */
const XX_PERSON_DATA_OBJECT_TYPE = 'xx.XxPersonData';

/** Object type alias of the HOST of the side-car instance — one half of its unique key. */
const XX_PERSON_OBJECT_TYPE = 'xx.XxPerson';

/** Object type alias of the business type of the side-car instance — the other half of its key. */
const XX_PERSON_TYPE_OBJECT_TYPE = 'xx.XxPersonType';

/**
 * Lower-case fragment used to recognise the occupation field among the type's field definitions.
 * Used for the DIAGNOSTIC hint only — the form itself never consults it, the rendered control is
 * whatever the backend form metadata asks for.
 */
const XX_OCCUPATION_FIELD_NAME_FRAGMENT = 'beruf';

/**
 * "Berufe (TAA)" tab of the unified xx Side-Car Playground.
 *
 * Purpose: exercise the framework's SEARCH-backed value list end to end on a real, large list (the
 * Stuttgarter TAA Berufeliste, ~6.333 entries) inside a real, metadata-driven form.
 *
 * What happens, and why nothing here is hand-rolled:
 * 1. the `xx.XxPersonType` named {@link XX_PLAYGROUND_PERSON_TYPE_NAME} is resolved by name, and in
 *    the SAME batched QL request its persons and its field definitions are read;
 * 2. the `xx.XxPerson` rows of that type populate the selector;
 * 3. for the chosen person the internal typed side-car instance (`xx.XxPersonData`, unique per
 *    person + type) is resolved — found means EDIT, missing means CREATE;
 * 4. the instance is rendered by `<mvs-crud-object-generic>`. `XxPersonDataForm` extends
 *    `GenericTemplateTypeInstanceFormObject`, so the backend emits one form field per
 *    `xx.XxPersonTypeField` of the type, each carrying its `cc.FieldType`;
 * 5. for the occupation field that `cc.FieldType` points at a `cc.FieldValueList` whose
 *    `selectionMode` is `search`. `FormService` therefore sets `uiInputControl = "valueListSearch"`
 *    and transfers only the list id — no entries. `CcModule` maps that control name onto
 *    `MvsFormFieldValueListSearchComponent`, which does the server-side type-ahead
 *    (`POST /mvsa/cc/fieldValueLists/{id}/search`) and resolves the stored code back to its label;
 * 6. saving the form writes the value through the generic-attribute path into `cc.FieldValue`.
 *
 * Both foreign keys of the edited instance are pinned through {@link hostContextDto}: the pair
 * `(xx_person_id, type_id)` is the unique key `xx_person_data_u1`, so letting the user re-point
 * either FK from inside the form would contradict the selector above and end in a raw database
 * constraint error. The import object context fixes AND hides them, in CREATE as well as in EDIT.
 *
 * This component therefore contains NO autocomplete, NO Angular form and NO field rendering of its
 * own: it only selects the object whose form the framework then builds.
 */
@Component({
    selector: 'mvs-xx-playground-berufe',
    templateUrl: './xx-playground-berufe.component.html',
    styleUrls: ['./xx-playground-berufe.component.scss'],
    standalone: false
})
export class XxPlaygroundBerufeComponent implements OnInit, OnDestroy {

    /** Object type alias bound to the embedded generic CRUD form. */
    readonly personDataObjectType = XX_PERSON_DATA_OBJECT_TYPE;

    /** True once the initial type / person resolution has finished (successfully or not). */
    initialized = false;

    /** True while the type, persons or side-car instance are being resolved. */
    loading = false;

    /** German error message for the current step, or null when there is none. */
    errorMessage: string | null = null;

    /** German success message after a save, or null when there is none. */
    successMessage: string | null = null;

    /** The resolved demo person type, or null when it is not seeded on this environment. */
    personType: XxPersonTypeDto | null = null;

    /** The persons of {@link personType} that populate the selector. */
    persons: XxPersonDto[] = [];

    /** Currently selected person, or null before the first selection. */
    selectedPerson: XxPersonDto | null = null;

    /** Field definitions of {@link personType} — shown as a hint, never used to build the form. */
    typeFields: XxPersonTypeFieldDto[] = [];

    /** Id of the existing side-car instance; null puts the form into CREATE mode. */
    personDataId: number | null = null;

    /** Pre-set DTO for CREATE mode, carrying the host person and the type. Null in EDIT mode. */
    createDto: DtoDetail | null = null;

    /**
     * Parent context of the edited side-car instance: the host person AND its business type. Bound
     * to the form in BOTH modes so the framework pre-fills and HIDES the two foreign keys that make
     * up the unique key of the instance.
     */
    hostContextDto: DtoImportObjectContext | null = null;

    /**
     * Bumped whenever the edited object changes. The template tracks a single-element list by this
     * value, so Angular DESTROYS and rebuilds the form instead of re-using it — otherwise the
     * previous person's field values would survive the switch.
     */
    formInstanceKey = 0;

    /**
     * Subscription slot of the type-context load. Single slot on purpose: a new load replaces (and
     * cancels) the previous one instead of piling up.
     */
    private contextSubscription: Subscription | null = null;

    /**
     * Subscription slot of the side-car resolution. Single slot on purpose: selecting persons in
     * quick succession must not accumulate subscriptions, and only the newest selection may win.
     */
    private personDataSubscription: Subscription | null = null;

    constructor(private playgroundService: XxSidecarPlaygroundService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /** Initialize Component. */
    initComponent(): void {
        this.refreshComponent();
    }

    /** Refresh Component: resolve the demo person type and everything hanging off it. */
    refreshComponent(): void {
        this.loadPersonTypeContext();
    }

    /** Destroy component: release both subscription slots. */
    ngOnDestroy(): void {
        this.releaseContextSubscription();
        this.releasePersonDataSubscription();
    }

    /**
     * Resolve the person type BY NAME together with its persons and field definitions — ONE batched
     * QL request, not several combined calls. Leaves {@link personType} null when the type is not
     * seeded, so the template shows a German hint instead of an empty form.
     */
    private loadPersonTypeContext(): void {

        this.loading = true;
        this.errorMessage = null;
        this.successMessage = null;

        this.releaseContextSubscription();

        this.contextSubscription = this.playgroundService
            .loadPersonTypeContextByName(XX_PLAYGROUND_PERSON_TYPE_NAME)
            .subscribe({
                next: (context) => {

                    this.personType = context.personType;
                    this.persons = context.persons ?? [];
                    this.typeFields = this.sortTypeFields(context.typeFields ?? []);
                    this.loading = false;
                    this.initialized = true;

                    if (this.personType && this.personType.id != null && this.persons.length > 0) {
                        this.selectedPerson = this.persons[0];
                        this.onPersonSelect();
                    }
                },
                error: () => {
                    this.personType = null;
                    this.persons = [];
                    this.typeFields = [];
                    this.loading = false;
                    this.initialized = true;
                    this.errorMessage = 'Der Personentyp und seine Personen konnten nicht geladen werden.';
                }
            });
    }

    /**
     * Selection handler: resolve the side-car instance of the chosen person and put the form into
     * EDIT mode when it exists, into CREATE mode (with person and type pre-set) when it does not.
     */
    onPersonSelect(): void {

        this.personDataId = null;
        this.createDto = null;
        this.hostContextDto = null;
        this.errorMessage = null;
        this.successMessage = null;

        this.releasePersonDataSubscription();

        if (!this.selectedPerson || this.selectedPerson.id == null || !this.personType || this.personType.id == null) {
            return;
        }

        const personId = this.selectedPerson.id;
        const typeId = this.personType.id;
        this.loading = true;

        // Pin BOTH foreign keys of the instance for the form: they are the unique key
        // xx_person_data_u1, so they must not stay editable in either mode.
        //
        // Entry ORDER matters, measured against the running backend on the form template endpoint:
        // when two context entries carry the SAME numeric id (here the very first demo person, id 1,
        // together with the type, also id 1) the person-first order resolves only the person and
        // leaves `typeDtoId` at access=update, while the type-first order resolves both to
        // access=hidden. For every id pair that does not collide both orders behave identically, so
        // type-first is never worse. The underlying id collision is a backend context-resolution
        // defect, not something this surface can repair.
        this.hostContextDto = DtoImportObjectContext.createFromObjectIdentifiers(
            new ObjectIdentifier(XX_PERSON_TYPE_OBJECT_TYPE, typeId),
            new ObjectIdentifier(XX_PERSON_OBJECT_TYPE, personId)
        );

        this.personDataSubscription = this.playgroundService
            .loadPersonDataForPerson(personId, typeId)
            .subscribe({
                next: (personData) => {
                    if (personData && personData.id != null) {
                        this.personDataId = personData.id;
                    } else {
                        // CREATE mode: both FKs of the instance are pre-set, so the generic template
                        // form already knows its type on the very first render and can emit the
                        // type's fields.
                        this.createDto = new DtoDetail({
                            typeDtoId: typeId,
                            xxPersonDtoId: personId
                        } as Partial<DtoDetail>);
                    }
                    this.formInstanceKey++;
                    this.loading = false;
                },
                error: () => {
                    this.loading = false;
                    this.errorMessage = 'Die internen typisierten Daten der Person konnten nicht geladen werden.';
                }
            });
    }

    /**
     * Object change handler of the embedded generic CRUD form.
     *
     * @param event the change information emitted by the form
     */
    onChangedObject(event: ObjectChangeInformation): void {

        if (!event) {
            return;
        }

        if (event.action === ObjectChangeInformationActionEnum.created) {
            this.errorMessage = null;
            this.successMessage = 'Die Daten wurden angelegt. Der Beruf ist als cc.FieldValue gespeichert.';
            const createdId = event.after?.id;
            if (createdId != null) {
                // switch the surface from CREATE to EDIT on the freshly created instance
                this.personDataId = createdId;
                this.createDto = null;
                this.formInstanceKey++;
            }
            return;
        }

        if (event.action === ObjectChangeInformationActionEnum.updated) {
            this.errorMessage = null;
            this.successMessage = 'Die Daten wurden gespeichert. Der Beruf ist als cc.FieldValue gespeichert.';
            return;
        }

        if (event.action === ObjectChangeInformationActionEnum.deleted) {
            this.errorMessage = null;
            this.successMessage = 'Die Daten wurden gelöscht.';
            // back to CREATE mode for the same person
            this.onPersonSelect();
        }
    }

    /** Re-resolve everything — used by the "Neu laden" button. */
    reload(): void {
        this.selectedPerson = null;
        this.personDataId = null;
        this.createDto = null;
        this.hostContextDto = null;
        this.typeFields = [];
        this.persons = [];
        this.refreshComponent();
    }

    /**
     * Display label of a person: "Vorname Nachname", falling back to the id when both names are
     * empty.
     *
     * @param person the person row
     */
    resolvePersonLabel(person: XxPersonDto | null): string {

        if (!person) {
            return '';
        }

        const label = [person.firstName, person.lastName]
            .filter(part => part !== null && part !== undefined && String(part).trim() !== '')
            .join(' ')
            .trim();

        return label !== '' ? label : `Person ${person.id}`;
    }

    /**
     * True when the given field definition is the occupation field. Drives the diagnostic hint only.
     *
     * @param typeField the `xx.XxPersonTypeField` row
     */
    isOccupationField(typeField: XxPersonTypeFieldDto): boolean {
        const name = typeField?.name;
        return name != null && String(name).toLowerCase().includes(XX_OCCUPATION_FIELD_NAME_FRAGMENT);
    }

    /** True when the type carries an occupation field at all — otherwise the tab warns about it. */
    get hasOccupationField(): boolean {
        return this.typeFields.some(typeField => this.isOccupationField(typeField));
    }

    /** German name of the demo person type, for the header line. */
    get personTypeName(): string {
        return XX_PLAYGROUND_PERSON_TYPE_NAME;
    }

    /** True while the form should be shown: a person is selected and we know its edit/create state. */
    get formVisible(): boolean {
        return !this.loading && this.selectedPerson != null && (this.personDataId != null || this.createDto != null);
    }

    /** German header of the form panel, naming the person and the mode. */
    get formHeader(): string {

        const person = this.resolvePersonLabel(this.selectedPerson);
        const mode = this.personDataId != null ? 'bearbeiten' : 'anlegen';

        return `Interne typisierte Daten ${mode} — ${person}`;
    }

    /**
     * Field definitions ordered by their configured field order, so the hint list matches the order
     * the form renders them in.
     *
     * @param typeFields the unsorted field definitions
     */
    private sortTypeFields(typeFields: XxPersonTypeFieldDto[]): XxPersonTypeFieldDto[] {
        return [...typeFields].sort((left, right) => {
            const leftOrder = left?.fieldOrder ?? 0;
            const rightOrder = right?.fieldOrder ?? 0;
            return leftOrder - rightOrder;
        });
    }

    /** Release the type-context subscription slot, if it holds a subscription. */
    private releaseContextSubscription(): void {
        if (this.contextSubscription) {
            this.contextSubscription.unsubscribe();
            this.contextSubscription = null;
        }
    }

    /** Release the side-car subscription slot, if it holds a subscription. */
    private releasePersonDataSubscription(): void {
        if (this.personDataSubscription) {
            this.personDataSubscription.unsubscribe();
            this.personDataSubscription = null;
        }
    }
}
